"use client";

/**
 * Global toast state machine + `useToast` hook.
 *
 * Implementation note: this is the canonical shadcn-ui pattern. State
 * lives in a module-level reducer, mutated via a small dispatcher and
 * subscribed to by every `useToast()` consumer through a list of
 * listener callbacks. The hook returns:
 *
 *   - `toast(props)` — fires a new toast. Returns { id, dismiss, update }
 *                     so callers can imperatively dismiss/update later.
 *   - `dismiss(id?)` — closes a specific toast (or all of them when
 *                      called with no args).
 *   - `toasts` — current list, consumed by `<Toaster />`.
 *
 * Why module-level state instead of context: any component (including
 * server-action callbacks fired far outside any provider) needs to be
 * able to call `toast()`. A singleton store keeps the call site as
 * `toast({ ... })` with no Provider plumbing. Subscribers use the hook
 * to re-render when state changes.
 *
 * Auto-dismiss: each Radix `<Toast>` accepts a `duration` (ms). The
 * default in `<Toaster>` is 5000ms — long enough to read a sentence,
 * short enough to disappear before the user gets annoyed. Callers
 * may still pass `duration` to override per-toast.
 *
 * The `TOAST_LIMIT = 3` cap is the standard shadcn knob: above three
 * stacked toasts the screen turns into noise. New ones evict the
 * oldest.
 */

import * as React from "react";
import type { ToastProps } from "@/components/ui/toast";

const TOAST_LIMIT = 3;
// How long a dismissed toast lingers (in the "open=false" state) before
// it's removed from the array. Matches the Radix close animation; if we
// drop it from the array sooner the exit animation skips.
const TOAST_REMOVE_DELAY = 1_000;

type ToastInput = Omit<ToastProps, "id"> & {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
};

type Toast = ToastInput & { id: string; open: boolean };

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

let counter = 0;
function genId(): string {
  counter = (counter + 1) % Number.MAX_SAFE_INTEGER;
  return counter.toString();
}

type Action =
  | { type: typeof actionTypes.ADD_TOAST; toast: Toast }
  | { type: typeof actionTypes.UPDATE_TOAST; toast: Partial<Toast> & { id: string } }
  | { type: typeof actionTypes.DISMISS_TOAST; toastId?: string }
  | { type: typeof actionTypes.REMOVE_TOAST; toastId?: string };

interface State {
  toasts: Toast[];
}

const removalTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

function queueRemoval(toastId: string) {
  if (removalTimeouts.has(toastId)) return;
  const timeout = setTimeout(() => {
    removalTimeouts.delete(toastId);
    dispatch({ type: actionTypes.REMOVE_TOAST, toastId });
  }, TOAST_REMOVE_DELAY);
  removalTimeouts.set(toastId, timeout);
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case actionTypes.UPDATE_TOAST:
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t,
        ),
      };

    case actionTypes.DISMISS_TOAST: {
      const { toastId } = action;
      if (toastId) {
        queueRemoval(toastId);
      } else {
        state.toasts.forEach((t) => queueRemoval(t.id));
      }
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? { ...t, open: false }
            : t,
        ),
      };
    }

    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) return { ...state, toasts: [] };
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
}

const listeners: Array<(state: State) => void> = [];
let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

/**
 * Imperatively show a toast. Returns the toast id and a dismiss
 * function for late cancellation (e.g. an "Undo" toast that's no
 * longer relevant once the user navigates away).
 */
function toast(props: ToastInput) {
  const id = props.id ?? genId();
  const dismiss = () => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });
  const update = (next: Partial<ToastInput>) =>
    dispatch({
      type: actionTypes.UPDATE_TOAST,
      toast: { ...next, id },
    });

  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: {
      ...props,
      id,
      open: true,
      // Radix's Toast root is a controlled component: it doesn't
      // self-close, it just calls `onOpenChange(false)` when the
      // duration timer fires, the X is clicked, or the user swipes
      // away. Without this callback wired to our reducer, none of
      // those close paths actually dismiss the toast — it stays
      // pinned with `open=true` forever (the bug we hit pre-fix:
      // 5s timer didn't dismiss, X did nothing).
      onOpenChange: (next: boolean) => {
        if (!next) dismiss();
      },
    },
  });

  return { id, dismiss, update };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const i = listeners.indexOf(setState);
      if (i > -1) listeners.splice(i, 1);
    };
  }, []);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: actionTypes.DISMISS_TOAST, toastId }),
  };
}

export { useToast, toast };
